/* eslint-disable react-hooks/exhaustive-deps */
import { loadStripe, Stripe, StripeCardElement } from "@stripe/stripe-js";
import _ from "lodash";
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { updateProjectStatus } from "store/customerProject/reducer";
import { removeCardInfoRequest } from "store/user/reducer";
import { setAlertMessageRequest } from "store/ui/reducer";
import type { PaymentStepSelectedPaymentInfo } from "../types";

export function useStripePaymentForm(params: {
  cardsCount: number;
  userId?: string | number | null;
  onPickPaymentInfo: (info: PaymentStepSelectedPaymentInfo | null) => void;
}) {
  const { cardsCount, userId, onPickPaymentInfo } = params;
  const dispatch = useDispatch();

  const [openCardInputForm, setOpenCardInputForm] = useState(false);
  const [selectedPaymentInfo, setSelectedPaymentInfo] = useState<any>(null);

  const [stripe, setStripe] = useState<Stripe | null>(null);
  const [cardElement, setCardElement] = useState<StripeCardElement | null>(null);
  const [cardHolderName, setCardHolderName] = useState("");
  const [cardBrand, setCardBrand] = useState("");

  const stripeCleanupRef = useRef<null | (() => void)>(null);

  useEffect(() => {
    if (cardsCount < 1) {
      setOpenCardInputForm(true);
    } else {
      setOpenCardInputForm(false);
    }
  }, [cardsCount]);

  useEffect(() => {
    onPickPaymentInfo(selectedPaymentInfo);
  }, [selectedPaymentInfo]);

  useEffect(() => {
    if (openCardInputForm) {
      setSelectedPaymentInfo(null);

      dispatch(
        updateProjectStatus({
          saveCardAsDefault: false,
          paymentOption: null,
        })
      );

      initStripe();
      return;
    }

    if (stripeCleanupRef.current) {
      stripeCleanupRef.current();
      stripeCleanupRef.current = null;
    }
    setStripe(null);
    setCardElement(null);
  }, [openCardInputForm]);

  const initStripe = async () => {
    if (stripeCleanupRef.current) {
      stripeCleanupRef.current();
      stripeCleanupRef.current = null;
    }

    const stripePublishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE;
    if (!stripePublishableKey) {
      dispatch(
        setAlertMessageRequest({
          type: "error",
          message: "Stripe publishable key is not configured.",
        })
      );
      return;
    }

    const stripeInstance = await loadStripe(stripePublishableKey);
    if (!stripeInstance) return;

    const elements = stripeInstance.elements();

    const elementStyle = {
      base: {
        iconColor: "#000",
        color: "#000",
        fontWeight: 400,
        fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
        fontSize: "16px",
        lineHeight: "30px",
        letterSpacing: "normal",
        fontSmoothing: "antialiased",
        "::placeholder": {
          color: "#9CA3AF",
        },
        ":-webkit-autofill": {
          color: "#777",
        },
      },
      invalid: {
        iconColor: "#F00",
        color: "#F00",
      },
    };

    const cardNumberEl = elements.create("cardNumber", {
      style: elementStyle,
      placeholder: "1234 1234 1234 1234",
    });
    const cardExpiryEl = elements.create("cardExpiry", {
      style: elementStyle,
      placeholder: "06/28",
    });
    const cardCvcEl = elements.create("cardCvc", {
      style: elementStyle,
      placeholder: "369",
    });

    cardNumberEl.mount("#payment-card-number-element");
    cardExpiryEl.mount("#payment-card-expiry-element");
    cardCvcEl.mount("#payment-card-cvc-element");

    let isCardNumberComplete = false;
    let isCardExpiryComplete = false;
    let isCardCvcComplete = false;

    const maybeSetReady = () => {
      if (isCardNumberComplete && isCardExpiryComplete && isCardCvcComplete) {
        setStripe(stripeInstance);
        setCardElement(cardNumberEl as any);
      } else {
        setStripe(null);
        setCardElement(null);
      }
    };

    cardNumberEl.on("change", (event: any) => {
      isCardNumberComplete = !!event.complete;
      if (event.brand) setCardBrand(event.brand);
      maybeSetReady();
    });
    cardExpiryEl.on("change", (event: any) => {
      isCardExpiryComplete = !!event.complete;
      maybeSetReady();
    });
    cardCvcEl.on("change", (event: any) => {
      isCardCvcComplete = !!event.complete;
      maybeSetReady();
    });

    stripeCleanupRef.current = () => {
      try {
        cardNumberEl.unmount();
        cardExpiryEl.unmount();
        cardCvcEl.unmount();
      } catch (e) {
      }
    };
  };

  useEffect(() => {
    if (stripe && cardElement && !_.isEmpty(cardHolderName)) {
      setSelectedPaymentInfo({
        cardHolderName,
        stripe,
        cardElement,
      });
    } else {
      setSelectedPaymentInfo(null);
    }
  }, [cardHolderName, stripe, cardElement]);

  const onSelectSavedCard = (card: any) => {
    setOpenCardInputForm(false);
    setSelectedPaymentInfo(card);
    dispatch(updateProjectStatus({ paymentOption: card }));
  };

  const onRemoveCard = (cardId: number) => {
    if (!userId) return;
    dispatch(
      removeCardInfoRequest({
        user_id: userId,
        card_id: cardId,
      })
    );
  };

  const onToggleAddNewCard = () => {
    setOpenCardInputForm((prev) => !prev);
  };

  return {
    openCardInputForm,
    selectedPaymentInfo,
    cardBrand,
    cardHolderName,
    setCardHolderName,
    onSelectSavedCard,
    onRemoveCard,
    onToggleAddNewCard,
  };
}
